import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import MaintenancePage from "../pages/MaintenancePage";
import PreviewGatePage, { hasPreviewCookie } from "../pages/PreviewGatePage";

// Environment variables (fallback only)
const ENV_MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true";
const PREVIEW_PASSWORD = process.env.REACT_APP_PREVIEW_GATE_PASSWORD || "";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Helper to build API path - always use /api prefix
const getApiPath = (path) => {
  // Backend always expects /api prefix on all routes
  return `${API_URL}/api${path}`;
};

// Preview/staging subdomains that require password gate
const PREVIEW_HOSTS = ["preview.amidex.org", "staging.amidex.org"];

// Routes that bypass maintenance mode
const MAINTENANCE_BYPASS_ROUTES = [
  "/admin",
  "/login",
  "/privacy",
  "/terms",
  "/checkout/external",  // External payment processor for partner sites (BioInfinitylab)
];

/**
 * MaintenanceGuard - Wraps the application to handle:
 * 1. Maintenance mode (production) - Shows maintenance page for public routes
 * 2. Preview gate (staging/preview subdomain) - Requires password to access
 */
const MaintenanceGuard = ({ children }) => {
  const location = useLocation();
  const [maintenanceMode, setMaintenanceMode] = useState(ENV_MAINTENANCE_MODE);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Get current hostname
  const hostname = window.location.hostname;
  const isPreviewHost = PREVIEW_HOSTS.includes(hostname);
  
  // Check preview cookie synchronously on initial render
  const initialPreviewAuth = useMemo(() => {
    if (isPreviewHost && PREVIEW_PASSWORD) {
      return hasPreviewCookie();
    }
    return false;
  }, [isPreviewHost]);
  
  const [previewAuthorized, setPreviewAuthorized] = useState(initialPreviewAuth);

  // Fetch maintenance status from backend
  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const response = await fetch(getApiPath('/settings/maintenance/status'));
        if (response.ok) {
          const data = await response.json();
          setMaintenanceMode(data.maintenance_enabled);
          if (data.maintenance_message) {
            setMaintenanceMessage(data.maintenance_message);
          }
        }
      } catch (error) {
        // If API fails, fall back to environment variable
        console.warn("Could not fetch maintenance status, using env fallback");
        setMaintenanceMode(ENV_MAINTENANCE_MODE);
      } finally {
        setLoading(false);
      }
    };
    
    checkMaintenanceStatus();
  }, []);

  // Check if current route bypasses maintenance
  const currentPath = location.pathname;
  const bypassesMaintenance = MAINTENANCE_BYPASS_ROUTES.some(
    (route) => currentPath === route || currentPath.startsWith(`${route}/`)
  );

  // Show loading spinner while checking maintenance status
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-700 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  // CASE 1: Preview/Staging subdomain - require password gate
  if (isPreviewHost && PREVIEW_PASSWORD) {
    if (!previewAuthorized) {
      return (
        <PreviewGatePage 
          onSuccess={() => setPreviewAuthorized(true)} 
        />
      );
    }
    // Authorized - show full site
    return <>{children}</>;
  }

  // CASE 2: Production with maintenance mode enabled
  if (maintenanceMode) {
    // Allow admin and other bypass routes
    if (bypassesMaintenance) {
      return <>{children}</>;
    }
    // Show maintenance page for all other routes
    return <MaintenancePage customMessage={maintenanceMessage} />;
  }

  // CASE 3: Normal operation - no maintenance, not preview host
  return <>{children}</>;
};

export default MaintenanceGuard;
